import React from "react";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { RadixButton } from "./RadixButton";
import { RadixInput } from "./RadixInput";
import "./radixComboBox.css";
export var RadixComboBox = React.forwardRef(function (props, ref) {
    var id = props.id, value = props.value, inputValue = props.inputValue, options = props.options, onInputValueChange = props.onInputValueChange, onValueChange = props.onValueChange, onBlur = props.onBlur, onOpenChange = props.onOpenChange, onFocus = props.onFocus, onInputMouseDown = props.onInputMouseDown, label = props.label, placeholder = props.placeholder, autoComplete = props.autoComplete, _a = props.noResultsMessage, noResultsMessage = _a === void 0 ? "No matching options found." : _a, ariaInvalid = props.ariaInvalid, ariaDescribedBy = props.ariaDescribedBy;
    var wrapperRef = React.useRef(null);
    var inputElementRef = React.useRef(null);
    var _b = React.useState(false), isOpen = _b[0], setIsOpen = _b[1];
    var _c = React.useState(-1), highlightedIndex = _c[0], setHighlightedIndex = _c[1];
    var setOpen = React.useCallback(function (nextOpen) {
        setIsOpen(nextOpen);
        if (!nextOpen) {
            setHighlightedIndex(-1);
        }
        if (onOpenChange) {
            onOpenChange(nextOpen);
        }
    }, [onOpenChange]);
    React.useEffect(function () {
        if (!isOpen) {
            return;
        }
        var handleDocumentMouseDown = function (event) {
            var target = event.target;
            if (!wrapperRef.current || wrapperRef.current.contains(target)) {
                return;
            }
            setOpen(false);
        };
        document.addEventListener("mousedown", handleDocumentMouseDown);
        return function () {
            document.removeEventListener("mousedown", handleDocumentMouseDown);
        };
    }, [isOpen, setOpen]);
    var setInputRef = React.useCallback(function (input) {
        inputElementRef.current = input;
        if (typeof ref === "function") {
            ref(input);
            return;
        }
        if (ref) {
            ref.current = input;
        }
    }, [ref]);
    var hasStatusMessage = noResultsMessage.trim().length > 0;
    var applySelection = React.useCallback(function (nextValue) {
        onValueChange(nextValue);
        setOpen(false);
    }, [onValueChange, setOpen]);
    return (React.createElement("div", { className: "radix-combobox", ref: wrapperRef },
        React.createElement(RadixInput, { id: id, ref: setInputRef, label: label, placeholder: placeholder, value: inputValue, onFocus: function () {
                if (onFocus) {
                    onFocus();
                }
                setOpen(true);
            }, onMouseDown: function () {
                if (onInputMouseDown) {
                    onInputMouseDown();
                }
            }, onChange: function (event) {
                onInputValueChange(event.target.value);
                setOpen(true);
            }, onBlur: function () {
                if (onBlur) {
                    onBlur();
                }
            }, onKeyDown: function (event) {
                if (!isOpen && event.key === "ArrowDown") {
                    setOpen(true);
                    setHighlightedIndex(0);
                    event.preventDefault();
                    return;
                }
                if (!isOpen) {
                    return;
                }
                if (event.key === "ArrowDown") {
                    setHighlightedIndex(function (current) {
                        var nextIndex = current + 1;
                        if (nextIndex >= options.length) {
                            return 0;
                        }
                        return nextIndex;
                    });
                    event.preventDefault();
                }
                if (event.key === "ArrowUp") {
                    setHighlightedIndex(function (current) {
                        var nextIndex = current - 1;
                        if (nextIndex < 0) {
                            return options.length - 1;
                        }
                        return nextIndex;
                    });
                    event.preventDefault();
                }
                if (event.key === "Enter" && highlightedIndex >= 0 && options[highlightedIndex]) {
                    applySelection(options[highlightedIndex].value);
                    event.preventDefault();
                }
                if (event.key === "Escape") {
                    setOpen(false);
                    event.preventDefault();
                }
            }, rightElement: React.createElement("button", { type: "button", className: "radix-combobox__toggle", "aria-label": "Toggle options", onMouseDown: function (event) {
                    event.preventDefault();
                    if (onInputMouseDown) {
                        onInputMouseDown();
                    }
                    if (inputElementRef.current) {
                        inputElementRef.current.focus();
                    }
                    setOpen(!isOpen);
                } },
                React.createElement(ChevronDownIcon, { className: "radix-combobox__caret" })), "aria-invalid": ariaInvalid, "aria-describedby": ariaDescribedBy, autoComplete: autoComplete }),
        isOpen && (options.length > 0 || hasStatusMessage) ? (React.createElement("div", { className: "radix-combobox__menu", role: "listbox", "aria-label": "".concat(label || "Combo box", " options") },
            options.length === 0 && hasStatusMessage ? (React.createElement("p", { className: "radix-combobox__status" }, noResultsMessage)) : null,
            options.map(function (option, index) {
                var isSelected = option.value === value;
                var isHighlighted = index === highlightedIndex;
                return (React.createElement(RadixButton, { key: option.value, type: "button", className: [
                        "radix-combobox__option",
                        isSelected ? "radix-combobox__option--selected" : "",
                        isHighlighted ? "radix-combobox__option--highlighted" : "",
                    ].filter(Boolean).join(" "), onMouseDown: function (event) {
                        event.preventDefault();
                    }, onMouseEnter: function () {
                        setHighlightedIndex(index);
                    }, onClick: function () {
                        applySelection(option.value);
                    } }, option.label));
            }))) : null));
});
RadixComboBox.displayName = "RadixComboBox";
